<script setup>
import { computed, onMounted, ref, watch } from 'vue'

const { $bgaApi } = useNuxtApp()

const userFirstName = ref('')
const summary = ref(null)
const pendingVouchers = ref([])
const pendingTotal = ref(0)

const summaryLoading = ref(false)
const pendingLoading = ref(false)
const summaryError = ref('')
const pendingError = ref('')
const refreshing = ref(false)

const merchantSelection = ref('')
const merchantOptions = ref([])
const merchantLoading = ref(false)
const merchantError = ref('')

const summaryData = computed(() => summary.value || {})
const integrations = computed(() => summaryData.value.integrations || {})
const genericPending = computed(() => summaryData.value.generic_vouchers_pending || {})
const transactionLogs = computed(() => summaryData.value.transaction_logs || {})
const postedVouchers = computed(() => summaryData.value.posted_vouchers_7d || {})

const overdueList = computed(() => integrations.value.overdue_list || [])
const latestErrors = computed(() => transactionLogs.value.latest_errors || [])

const selectedMerchantLabel = computed(() => {
  if (!merchantSelection.value) return ''
  const selected = merchantOptions.value.find(
    (option) => String(option.value) === String(merchantSelection.value),
  )
  return selected?.label || ''
})

const subscriptionIdParam = computed(() => {
  if (!merchantSelection.value) return null
  const raw = String(merchantSelection.value).trim()
  if (!/^\d+(\.0+)?$/.test(raw)) return null
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return String(parsed)
})

const scopeLabel = computed(() => {
  if (selectedMerchantLabel.value) {
    return selectedMerchantLabel.value
  }
  return 'F\u00f6retag'
})

const asOfLabel = computed(() => formatDateTime(summaryData.value.as_of))

const heroHighlights = computed(() => [
  {
    key: 'overdue',
    label: 'F\u00f6rsenade',
    value: formatCount(integrations.value.overdue_count),
  },
  {
    key: 'pending',
    label: 'V\u00e4ntande',
    value: formatCount(genericPending.value.count),
  },
  {
    key: 'errors',
    label: 'Fel (24h)',
    value: formatCount(transactionLogs.value.error_count_24h),
  },
])

const statCards = computed(() => {
  const cutoff = integrations.value.overdue_cutoff_date
  const pendingOldest = genericPending.value.oldest_transaction_date
  const latestError = latestErrors.value[0]?.created_at
  const postedLatest = postedVouchers.value.latest_transaction_date
  const postedFrom = postedVouchers.value.from_date

  return [
    {
      key: 'overdue',
      label: 'F\u00f6rsenade integrationer',
      value: formatCount(integrations.value.overdue_count),
      subtitle: cutoff ? `Gr\u00e4ns ${formatDate(cutoff)}` : 'Mer \u00e4n 1 dag sedan senaste k\u00f6rning',
      tone: 'warning',
      icon: 'hero-clock',
    },
    {
      key: 'never-run',
      label: 'Aldrig k\u00f6rda',
      value: formatCount(integrations.value.never_run_count),
      subtitle: 'Beh\u00f6ver f\u00f6rsta synk',
      tone: 'info',
      icon: 'hero-arrow-path',
    },
    {
      key: 'pending-vouchers',
      label: 'V\u00e4ntande verifikationer',
      value: formatCount(genericPending.value.count),
      subtitle: pendingOldest ? `\u00c4ldsta ${formatDate(pendingOldest)}` : 'V\u00e4ntar p\u00e5 bokf\u00f6ring',
      tone: 'primary',
      icon: 'hero-document-text',
    },
    {
      key: 'errors',
      label: 'Fel (24h)',
      value: formatCount(transactionLogs.value.error_count_24h),
      subtitle: latestError ? `Senast ${formatDateTime(latestError)}` : 'Inga nya fel',
      tone: 'error',
      icon: 'hero-exclamation-triangle',
    },
    {
      key: 'posted',
      label: 'Bokf\u00f6rda verifikationer (7d)',
      value: formatCount(postedVouchers.value.count),
      subtitle: postedLatest
        ? `Senast ${formatDate(postedLatest)}`
        : postedFrom
          ? `Sedan ${formatDate(postedFrom)}`
          : 'Senaste 7 dagarna',
      tone: 'success',
      icon: 'hero-clipboard-document-check',
    },
  ]
})

function formatCount(value) {
  if (typeof value !== 'number') return '--'
  return value.toLocaleString('sv-SE')
}

function formatDate(value) {
  if (!value) return '--'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  })
}

function formatDateTime(value) {
  if (!value) return '--'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatAmount(value) {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '--'
  const formatted = amount.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${formatted} SEK`
}


function integrationLabel(item) {
  const app = String(item?.app || '').toLowerCase()
  const appLabel = app ? app.charAt(0).toUpperCase() + app.slice(1) : 'Integration'
  const merchant = item?.merchant_id ? ` - ${item.merchant_id}` : ''
  return `${appLabel}${merchant}`
}

function integrationLink(item) {
  const app = String(item?.app || '').toLowerCase()
  if (!item?.settings_id) return null
  if (app === 'klarna') return `/integration-settings/klarna/${item.settings_id}`
  if (app === 'adyen') return `/integration-settings/adyen/${item.settings_id}`
  return null
}

function voucherTypeLabel(value) {
  if (!value) return 'Verifikation'
  const normalized = String(value).toLowerCase()
  if (normalized === 'sale') return 'F\u00f6rs\u00e4ljning'
  if (normalized === 'payout') return 'Utbetalning'
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

function voucherDate(voucher) {
  return voucher?.transaction_datetime_obj || voucher?.transaction_date || voucher?.date_created
}

async function fetchSummary() {
  summaryLoading.value = true
  summaryError.value = ''

  try {
    const params = {}
    if (subscriptionIdParam.value) params.subscription_id = subscriptionIdParam.value
    const response = await $bgaApi.get('bga/home/summary/', { params })
    summary.value = response.data
  } catch (error) {
    summaryError.value = 'Kunde inte hämta hemöversikten. Försök igen.'
  } finally {
    summaryLoading.value = false
  }
}

async function fetchPendingVouchers() {
  pendingLoading.value = true
  pendingError.value = ''

  try {
    const params = { limit: 6 }
    if (subscriptionIdParam.value) params.subscription_id = subscriptionIdParam.value
    const response = await $bgaApi.get('bga/generic-vouchers/pending/', { params })
    pendingVouchers.value = response.data?.results || []
    pendingTotal.value = response.data?.count || 0
  } catch (error) {
    pendingError.value = 'Kunde inte hämta väntande verifikationer.'
  } finally {
    pendingLoading.value = false
  }
}

async function fetchAll() {
  await Promise.all([fetchSummary(), fetchPendingVouchers()])
}

async function refreshSummary() {
  if (refreshing.value) return
  refreshing.value = true
  summaryError.value = ''

  try {
    const payload = {}
    if (subscriptionIdParam.value) payload.subscription_id = subscriptionIdParam.value
    const response = await $bgaApi.post('bga/home/summary/refresh/', payload)
    summary.value = response.data
    await fetchPendingVouchers()
  } catch (error) {
    summaryError.value = 'Kunde inte uppdatera översikten just nu.'
  } finally {
    refreshing.value = false
  }
}

async function fetchMerchantOptions() {
  merchantLoading.value = true
  merchantError.value = ''

  try {
    const response = await $bgaApi.get('bga/integration-settings')
    const data = response.data || {}
    const options = []

    const addOptions = (items, typeLabel) => {
      if (!Array.isArray(items)) return
      items.forEach((item) => {
        if (item?.active === false) return
        if (!item?.subscription) return
        const merchantId = String(item?.merchant_id || '').trim()
        const fallback = item?.id ? `ID ${item.id}` : 'Okänd'
        const label = `${typeLabel} - ${merchantId || fallback}`
        options.push({ value: String(item.subscription), label })
      })
    }

    addOptions(data.klarna_settings, 'Klarna')
    addOptions(data.adyen_settings, 'Adyen')

    const unique = new Map()
    options.forEach((option) => {
      if (!unique.has(option.value)) unique.set(option.value, option)
    })

    merchantOptions.value = Array.from(unique.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'sv-SE'),
    )
  } catch (error) {
    merchantError.value = 'Kunde inte hämta merchant-ID.'
  } finally {
    merchantLoading.value = false
  }
}

function clearMerchantSelection() {
  merchantSelection.value = ''
}

const fetchUserFirstName = () => {
  const rawUser = localStorage.getItem('user')
  if (!rawUser) return
  try {
    const user = JSON.parse(rawUser)
    if (user && user.first_name) {
      userFirstName.value = user.first_name
    }
  } catch (error) {
    // Ignore malformed user data.
  }
}

onMounted(() => {
  fetchUserFirstName()
  fetchMerchantOptions()
  fetchAll()
})

watch(merchantSelection, () => {
  fetchAll()
})
</script>

<template>
  <div class="dashboard-stack home-dashboard">
    <div class="dashboard-header home-header">
      <div class="dashboard-header__titles">
        <p class="home-kicker">Bokföringsöversikt</p>
        <h1 class="dashboard-page__title">
          Välkommen{{ userFirstName ? `, ${userFirstName}` : '' }}
        </h1>
        <p class="dashboard-page__subtitle lp-body">
          Följ integrationshälsa, väntande verifikationer och senaste felaktivitet på ett ställe.
        </p>
      </div>

      <div class="dashboard-header__actions">
        <div class="dashboard-header__meta">
          <span class="dashboard-header__meta-label">Omfattning</span>
          <span class="dashboard-header__meta-value">{{ scopeLabel }}</span>
        </div>
        <div class="dashboard-header__meta">
          <span class="dashboard-header__meta-label">Uppdaterad</span>
          <span class="dashboard-header__meta-value">{{ asOfLabel }}</span>
        </div>
        <AppButton variant="secondary" size="sm" :loading="refreshing" @click="refreshSummary">
          <AppIcon name="hero-arrow-path" size="md" />
          Uppdatera
        </AppButton>
      </div>
    </div>

    <AppCard class="home-hero">
      <div class="home-hero__grid">
        <div class="home-hero__content">
          <p class="home-kicker home-hero__kicker">Aktuell översikt</p>
          <h2 class="home-hero__title">Håll bokföringsflödet förutsägbart.</h2>
          <p class="home-hero__subtitle">
            Följ dagliga körningar, bokföringar och feltoppar innan de blir månadsslutproblem.
          </p>

          <div class="home-hero__summary">
            <div
              v-for="(item, index) in heroHighlights"
              :key="item.key"
              class="home-hero__summary-item home-stagger"
              :style="{ '--delay': `${index * 0.05}s` }"
            >
              <span class="home-hero__summary-label">{{ item.label }}</span>
              <span class="home-hero__summary-value">{{ item.value }}</span>
            </div>
          </div>
        </div>

        <div class="home-hero__panel">
          <div class="home-hero__panel-header">
            <div>
              <p class="home-hero__panel-label">Filtrerad omfattning</p>
              <p class="home-hero__panel-value">{{ scopeLabel }}</p>
            </div>
            <AppIcon name="hero-view-columns" size="lg" />
          </div>

          <p class="home-hero__panel-meta">Senast uppdaterad {{ asOfLabel }}</p>

          <div class="home-hero__panel-form">
            <AppCombobox
              v-model="merchantSelection"
              label="Merchant-ID"
              placeholder="Alla integrationer"
              empty-label="Inga träffar."
              :options="merchantOptions"
              :disabled="merchantLoading"
              :error="merchantError"
              density="compact"
            />

            <div class="home-hero__panel-actions">
              <AppButton
                variant="ghost"
                size="sm"
                type="button"
                :disabled="merchantLoading"
                @click="clearMerchantSelection"
              >
                Rensa filter
              </AppButton>
            </div>

            <p v-if="merchantLoading" class="home-hero__panel-hint">
              Hämtar merchant-ID...
            </p>
          </div>
        </div>
      </div>
    </AppCard>

    <div v-if="summaryError" class="app-alert app-alert--error">
      {{ summaryError }}
    </div>
    <div v-else-if="summaryLoading && !summary" class="app-alert">
      Hämtar senaste översikten...
    </div>

    <div class="dashboard-grid dashboard-grid--stats home-stat-grid">
      <AppCard
        v-for="(stat, index) in statCards"
        :key="stat.key"
        class="app-stat-card home-stat-card"
        :class="`app-stat-card--${stat.tone}`"
        :style="{ '--delay': `${index * 0.05}s` }"
      >
        <div class="app-stat-card__body">
          <span class="app-stat-card__label">{{ stat.label }}</span>
          <span class="app-stat-card__value">{{ stat.value }}</span>
          <span class="app-stat-card__subtitle">{{ stat.subtitle }}</span>
        </div>
        <div class="app-stat-card__icon">
          <AppIcon :name="stat.icon" size="lg" />
        </div>
      </AppCard>
    </div>

    <div class="dashboard-grid home-grid">
      <AppCard
        title="Integrationsbevakning"
        :subtitle="integrations.overdue_cutoff_date ? `F\u00f6rsenad sedan ${formatDate(integrations.overdue_cutoff_date)}` : 'F\u00f6rsenade integrationer \u00f6ver 24 timmar'"
      >
        <template #actions>
          <AppButton variant="ghost" size="sm" to="/settings">
            Öppna inställningar
          </AppButton>
        </template>

        <div v-if="overdueList.length" class="home-list">
          <div
            v-for="(item, index) in overdueList"
            :key="`${item.app}-${item.settings_id}`"
            class="home-list__item home-stagger"
            :style="{ '--delay': `${index * 0.04}s` }"
          >
            <div class="home-list__main">
              <div class="home-list__title">
                <AppIcon name="hero-clock" size="sm" />
                <span>{{ integrationLabel(item) }}</span>
              </div>
              <div class="home-list__meta">
                <span>Senast körning {{ formatDate(item.last_processed_date) }}</span>
                <span v-if="item.subscription_id">Abonnemang {{ item.subscription_id }}</span>
                <NuxtLink v-if="integrationLink(item)" :to="integrationLink(item)" class="home-link">
                  Öppna integration
                </NuxtLink>
              </div>
            </div>
            <AppChip variant="warning">
              {{ item.days_since_last_processed }} dagar
            </AppChip>
          </div>
        </div>
        <p v-else class="home-empty">
          Inga försenade integrationer. Allt har körts inom de senaste 24 timmarna.
        </p>
      </AppCard>

      <AppCard
        title="Senaste fel (24h)"
        subtitle="Senaste felhändelser i dina integrationer."
      >
        <template #actions>
          <AppButton variant="ghost" size="sm" to="/transaction-logs">
            Visa loggar
          </AppButton>
        </template>

        <div v-if="latestErrors.length" class="home-list">
          <div
            v-for="(item, index) in latestErrors"
            :key="item.id"
            class="home-list__item home-stagger"
            :style="{ '--delay': `${index * 0.04}s` }"
          >
            <div class="home-list__main">
              <div class="home-list__title">
                <AppIcon name="hero-exclamation-triangle" size="sm" />
                <span>{{ item.subject || 'Felhändelse' }}</span>
              </div>
              <div class="home-list__meta">
                <span class="home-list__message">{{ item.message }}</span>
              </div>
            </div>
            <div class="home-list__aside">
              <span class="home-list__time">{{ formatDateTime(item.created_at) }}</span>
              <AppChip variant="warning">Fel</AppChip>
            </div>
          </div>
        </div>
        <p v-else class="home-empty">
          Inga nya felhändelser de senaste 24 timmarna.
        </p>
      </AppCard>
    </div>

    <div class="dashboard-grid home-grid">
      <AppCard title="Väntande generiska verifikationer" subtitle="Bokföringsnummer saknas.">
        <template #actions>
          <AppButton variant="ghost" size="sm" to="/vouchers">
            Öppna verifikationer
          </AppButton>
        </template>

        <div v-if="pendingError" class="app-alert app-alert--error">
          {{ pendingError }}
        </div>
        <div v-else-if="pendingLoading && !pendingVouchers.length" class="app-alert">
          Hämtar väntande verifikationer...
        </div>

        <div v-if="pendingVouchers.length" class="home-list">
          <div
            v-for="(voucher, index) in pendingVouchers"
            :key="voucher.id"
            class="home-list__item home-stagger"
            :style="{ '--delay': `${index * 0.04}s` }"
          >
            <div class="home-list__main">
              <div class="home-list__title">
                <AppIcon name="hero-document-text" size="sm" />
                <span>{{ voucher.description || `Verifikation ${voucher.id}` }}</span>
              </div>
              <div class="home-list__meta">
                <span>Serie {{ voucher.series || '--' }}</span>
                <span>Datum {{ formatDate(voucherDate(voucher)) }}</span>
                <span v-if="voucher.subscription_id">Abonnemang {{ voucher.subscription_id }}</span>
              </div>
            </div>
            <div class="home-list__aside">
              <span class="home-list__amount">{{ formatAmount(voucher.total_amount_sek) }}</span>
              <AppChip variant="info">{{ voucherTypeLabel(voucher.voucher_type) }}</AppChip>
            </div>
          </div>
        </div>
        <p v-else-if="!pendingLoading" class="home-empty">
          Inga väntande verifikationer att granska.
        </p>
        <p v-if="pendingTotal > pendingVouchers.length" class="home-footnote">
          Visar {{ pendingVouchers.length }} av {{ pendingTotal }} väntande verifikationer.
        </p>
      </AppCard>

      <AppCard class="home-export">
        <div class="home-export__header">
          <div>
            <p class="home-kicker home-kicker--compact">Verifikationsexport</p>
            <h3 class="home-export__title">Redo för avstämning</h3>
            <p class="home-export__subtitle">
              Ladda ned senaste verifikationsexport och stäm av mot Fortnox eller ert ERP.
            </p>
          </div>
          <div class="home-export__icon">
            <AppIcon name="hero-arrow-down-tray" size="xl" />
          </div>
        </div>

        <div class="home-export__grid">
          <div class="home-export__metric">
            <span class="home-export__label">Bokförda senaste 7 dagar</span>
            <span class="home-export__value">{{ formatCount(postedVouchers.count) }}</span>
            <span class="home-export__meta">
              Senast {{ formatDate(postedVouchers.latest_transaction_date) }}
            </span>
          </div>
          <div class="home-export__metric">
            <span class="home-export__label">Väntande intervall</span>
            <span class="home-export__value">{{ formatDate(genericPending.oldest_transaction_date) }}</span>
            <span class="home-export__meta">
              Nyast {{ formatDate(genericPending.newest_transaction_date) }}
            </span>
          </div>
        </div>

        <div class="home-export__actions">
          <AppButton variant="navy" to="/vouchers">
            Öppna verifikationsexport
          </AppButton>
          <AppButton variant="outline" to="/transaction-logs">
            Granska loggar
          </AppButton>
        </div>
      </AppCard>
    </div>

    <AppCard title="Snabba åtgärder">
      <div class="dashboard-grid dashboard-grid--actions">
        <AppButton class="app-btn--block" variant="secondary" to="/vouchers">
          <AppIcon name="hero-document-text" size="md" />
          Granska verifikationer
        </AppButton>
        <AppButton class="app-btn--block" variant="secondary" to="/transaction-logs">
          <AppIcon name="hero-list-bullet" size="md" />
          Transaktionsloggar
        </AppButton>
        <AppButton class="app-btn--block" variant="secondary" to="/settings">
          <AppIcon name="hero-cog-6-tooth" size="md" />
          Integrationsinställningar
        </AppButton>
        <AppButton class="app-btn--block" variant="secondary" to="/vouchers">
          <AppIcon name="hero-arrow-down-tray" size="md" />
          Verifikationsexport
        </AppButton>
      </div>
    </AppCard>
  </div>
</template>

<style scoped>
.home-dashboard {
  --home-accent: rgba(72, 214, 243, 0.55);
  --home-accent-strong: rgba(72, 214, 243, 0.85);
  --home-ink: var(--lp-dashboard-text-high);
  --home-muted: var(--lp-dashboard-text-medium);
  --home-border: rgba(72, 214, 243, 0.2);
  --home-surface: var(--lp-dashboard-card-tint);
  --home-shadow: 0 18px 40px rgba(13, 13, 30, 0.08);
}

.home-header {
  position: relative;
}

.home-kicker {
  margin: 0 0 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--lp-color-blue);
}

.home-kicker--compact {
  margin-bottom: 6px;
}

.home-hero {
  position: relative;
  overflow: visible;
  padding: var(--lp-space-7);
  border: 1px solid var(--home-border);
  background: radial-gradient(circle at top left, rgba(72, 214, 243, 0.16) 0%, transparent 55%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(72, 214, 243, 0.08) 100%);
  box-shadow: var(--home-shadow);
  animation: home-hero-in 0.6s ease-out;
}

.home-hero::before,
.home-hero::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(72, 214, 243, 0.2) 0%, transparent 70%);
  opacity: 0.6;
  pointer-events: none;
  animation: home-orb 18s ease-in-out infinite;
}

.home-hero::before {
  width: 220px;
  height: 220px;
  top: -80px;
  right: 20%;
}

.home-hero::after {
  width: 280px;
  height: 280px;
  bottom: -140px;
  left: -60px;
  animation-delay: -6s;
}

.home-hero__grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: var(--lp-space-6);
  position: relative;
  z-index: 1;
}

.home-hero__title {
  margin: 0 0 var(--lp-space-3);
  font-size: clamp(1.6rem, 2.8vw, 2.2rem);
  font-weight: 800;
  color: var(--home-ink);
  letter-spacing: -0.02em;
}

.home-hero__subtitle {
  margin: 0 0 var(--lp-space-5);
  color: var(--home-muted);
  line-height: 1.6;
  max-width: 56ch;
}

.home-hero__summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--lp-space-3);
}

.home-hero__summary-item {
  padding: var(--lp-space-4);
  background: rgba(255, 255, 255, 0.85);
  border-radius: 14px;
  border: 1px solid rgba(72, 214, 243, 0.16);
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 10px 24px rgba(13, 13, 30, 0.06);
}

.home-hero__summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--home-muted);
  font-weight: 700;
}

.home-hero__summary-value {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--home-ink);
}

.home-hero__panel {
  background: rgba(255, 255, 255, 0.88);
  border-radius: 18px;
  border: 1px solid rgba(72, 214, 243, 0.16);
  padding: var(--lp-space-5);
  display: flex;
  flex-direction: column;
  gap: var(--lp-space-4);
  box-shadow: 0 12px 28px rgba(13, 13, 30, 0.08);
  backdrop-filter: blur(6px);
  position: relative;
  z-index: 3;
}

.home-hero__panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lp-space-3);
}

.home-hero__panel-label {
  margin: 0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 700;
  color: var(--home-muted);
}

.home-hero__panel-value {
  margin: 4px 0 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--home-ink);
}

.home-hero__panel-meta {
  margin: 0;
  font-size: 0.85rem;
  color: var(--home-muted);
}

.home-hero__panel-form {
  display: flex;
  flex-direction: column;
  gap: var(--lp-space-3);
}

.home-hero__panel-actions {
  display: flex;
  align-items: center;
  gap: var(--lp-space-2);
  flex-wrap: wrap;
}

.home-hero__panel-hint {
  margin: 0;
  font-size: 0.8rem;
  color: var(--home-muted);
}

.home-stat-card {
  animation: home-rise 0.45s ease both;
  animation-delay: var(--delay, 0s);
}

.home-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--lp-space-4);
}

.home-list {
  display: flex;
  flex-direction: column;
  gap: var(--lp-space-3);
}

.home-list__item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lp-space-3);
  padding: var(--lp-space-4);
  border-radius: 14px;
  border: 1px solid var(--lp-dashboard-border);
  background: var(--home-surface);
  animation: home-rise 0.4s ease both;
  animation-delay: var(--delay, 0s);
}

.home-list__main {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.home-list__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: var(--home-ink);
}

.home-list__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.82rem;
  color: var(--home-muted);
}

.home-list__message {
  color: var(--home-muted);
  max-width: 46ch;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.home-list__aside {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  min-width: 110px;
}

.home-list__amount {
  font-weight: 700;
  color: var(--home-ink);
}

.home-list__time {
  font-size: 0.78rem;
  color: var(--home-muted);
}

.home-link {
  font-weight: 600;
  color: var(--lp-color-blue);
  text-decoration: none;
}

.home-link:hover {
  text-decoration: underline;
}

.home-empty {
  margin: 0;
  color: var(--home-muted);
  font-size: 0.9rem;
}

.home-footnote {
  margin-top: var(--lp-space-3);
  font-size: 0.8rem;
  color: var(--home-muted);
}

.home-export {
  background: linear-gradient(135deg, rgba(72, 214, 243, 0.12) 0%, rgba(255, 255, 255, 0.9) 60%);
  border: 1px solid rgba(72, 214, 243, 0.18);
}

.home-export__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lp-space-4);
  margin-bottom: var(--lp-space-4);
}

.home-export__title {
  margin: 0 0 6px;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--home-ink);
}

.home-export__subtitle {
  margin: 0;
  color: var(--home-muted);
  line-height: 1.5;
}

.home-export__icon {
  inline-size: 54px;
  block-size: 54px;
  border-radius: 16px;
  background: rgba(72, 214, 243, 0.18);
  color: var(--lp-color-blue);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.home-export__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--lp-space-4);
  margin-bottom: var(--lp-space-5);
}

.home-export__metric {
  padding: var(--lp-space-4);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(72, 214, 243, 0.16);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.home-export__label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--home-muted);
  font-weight: 700;
}

.home-export__value {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--home-ink);
}

.home-export__meta {
  font-size: 0.82rem;
  color: var(--home-muted);
}

.home-export__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lp-space-3);
}

.home-stagger {
  animation: home-rise 0.4s ease both;
  animation-delay: var(--delay, 0s);
}

@keyframes home-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes home-hero-in {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes home-orb {
  0%, 100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(18px, -14px);
  }
}

@media (max-width: 1100px) {
  .home-hero__grid {
    grid-template-columns: 1fr;
  }

  .home-hero__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .home-grid {
    grid-template-columns: 1fr;
  }

  .home-export__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .home-hero {
    padding: var(--lp-space-5);
  }

  .home-hero__summary {
    grid-template-columns: 1fr;
  }

  .home-list__item {
    flex-direction: column;
    align-items: flex-start;
  }

  .home-list__aside {
    align-items: flex-start;
  }

  .home-export__header {
    flex-direction: column;
  }
}
</style>
